import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { findGame, type Game } from "../data/gamesData";
import TouchControls from "../components/TouchControls";
import NotFound from "./NotFound";

// js-dos is a global script (see scripts/copy-js-dos.mjs); these are the
// only bits of its API this page touches.
type DosProps = { setNoCloud?: (value: boolean) => void; stop?: () => Promise<void> };
type CommandInterface = { sendKeyEvent: (code: number, pressed: boolean) => void };
declare global {
  interface Window {
    Dos?: (element: HTMLElement, options: Record<string, unknown>) => DosProps;
  }
}

const JS_DOS_BASE = "/js-dos/";
// Emulator + scripts downloaded on first Play, on top of the game bundle.
const RUNTIME_MB = 2;

let runtimePromise: Promise<void> | null = null;

// Injected on demand so nothing heavy downloads until the visitor hits Play.
function loadRuntime(): Promise<void> {
  if (window.Dos) return Promise.resolve();
  if (runtimePromise) return runtimePromise;

  runtimePromise = new Promise<void>((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `${JS_DOS_BASE}js-dos.css`;
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.src = `${JS_DOS_BASE}js-dos.js`;
    script.onload = () => resolve();
    script.onerror = () => {
      runtimePromise = null;
      script.remove();
      reject(new Error("Failed to load js-dos"));
    };
    document.body.appendChild(script);
  });
  return runtimePromise;
}

type Status = "idle" | "loading" | "running" | "error";

const isTouchDevice = () => window.matchMedia("(pointer: coarse)").matches;

function Player({ game }: { game: Game }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dosRef = useRef<DosProps | null>(null);
  const ciRef = useRef<CommandInterface | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [touch] = useState(isTouchDevice);

  useEffect(() => {
    return () => {
      void dosRef.current?.stop?.();
      dosRef.current = null;
    };
  }, []);

  const play = async () => {
    setStatus("loading");
    try {
      await loadRuntime();
      if (!containerRef.current || !window.Dos) throw new Error("js-dos unavailable");
      const dos = window.Dos(containerRef.current, {
        url: `/games/${game.slug}.jsdos`,
        pathPrefix: `${JS_DOS_BASE}emulators/`,
        backend: "dosbox",
        autoStart: true,
        theme: "dark",
        thinSidebar: true,
        // All keyboard games; don't hijack the visitor's cursor.
        mouseCapture: false,
        onEvent: (event: string, ci?: CommandInterface) => {
          if (event === "ci-ready" && ci) ciRef.current = ci;
        },
      });
      dos.setNoCloud?.(true);
      dosRef.current = dos;
      setStatus("running");
    } catch (error) {
      console.error(`[games/${game.slug}]`, error);
      setStatus("error");
    }
  };

  const downloadMb = window.Dos ? game.sizeMb : game.sizeMb + RUNTIME_MB;

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 md:px-8">
      <Link to="/games" className="text-sm text-gray-400 hover:text-gray-200 transition-colors">
        ← All games
      </Link>
      <h1 className="h2 mt-3 mb-2">{game.title}</h1>
      <p className="text-gray-400 mb-6">
        {game.episode} ({game.year} shareware) by {game.publisher}, running in your browser via js-dos.
      </p>

      <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-white/10 bg-black">
        <div ref={containerRef} className="absolute inset-0" />

        {status !== "running" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
            {status === "error" ? (
              <>
                <p className="text-red-400">Couldn't start the game. Check your connection and try again.</p>
                <button
                  type="button"
                  onClick={play}
                  className="rounded-md bg-red-700 hover:bg-red-600 px-6 py-3 font-semibold transition-colors"
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={play}
                  disabled={status === "loading"}
                  className="rounded-md bg-red-700 hover:bg-red-600 disabled:opacity-60 disabled:cursor-wait px-8 py-4 text-lg font-bold tracking-wide transition-colors"
                >
                  {status === "loading" ? "Loading…" : "Play"}
                </button>
                <p className="text-sm text-gray-500">
                  About a {downloadMb.toFixed(1)} MB download. Nothing loads until you press Play.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {touch && status === "running" && (
        <TouchControls
          actions={game.touch.actions}
          extras={game.touch.extras}
          send={(code, pressed) => ciRef.current?.sendKeyEvent(code, pressed)}
        />
      )}

      <div className="mt-6 grid gap-2 text-sm text-gray-400 sm:grid-cols-2">
        {game.controls.map((c) => (
          <p key={c.action}>
            <span className="text-gray-200">{c.action}:</span> {c.keys}
          </p>
        ))}
      </div>

      <p className="mt-6 text-xs text-gray-600">
        {game.title} © {game.publisher}. Shareware episode distributed unmodified.
      </p>
    </div>
  );
}

function GamePlayer() {
  const { slug } = useParams();
  const game = findGame(slug);
  if (!game) return <NotFound />;
  // Keyed so switching games tears down the previous emulator.
  return <Player key={game.slug} game={game} />;
}

export default GamePlayer;
